import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatDate, formatTL, STATUS_LABELS } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import CustomerRewards from "../../components/CustomerRewards";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { ArrowLeft, Plus, AlertTriangle, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function CustomerDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ brand_id: "", type: "kombi", model: "", serial_no: "", install_date: "", warranty_until: "", notes: "" });

  const load = async () => {
    const { data } = await api.get(`/customers/${id}`);
    setData(data);
  };
  useEffect(() => { load(); api.get("/brands").then(r => setBrands(r.data)); }, [id]);

  const addDevice = async () => {
    if (!form.brand_id) { toast.error("Marka seçin"); return; }
    try {
      await api.post("/devices", { ...form, customer_id: id });
      toast.success("Cihaz eklendi");
      setOpen(false);
      setForm({ brand_id: "", type: "kombi", model: "", serial_no: "", install_date: "", warranty_until: "", notes: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Hata"); }
  };

  if (!data) return <div>Yükleniyor...</div>;
  const { customer, devices, jobs, has_repeated_fault } = data;

  const isWarrantyActive = (d) => {
    if (!d.warranty_until) return false;
    return new Date(d.warranty_until) >= new Date();
  };

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <Link to="/admin/customers" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Müşteriler
      </Link>

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900">{customer.name}</h1>
          <div className="text-slate-600 mt-1">{customer.phone} • {customer.email || "—"}</div>
          <div className="text-sm text-slate-500 mt-1">{[customer.address, customer.district, customer.city].filter(Boolean).join(", ")}</div>
        </div>
        {has_repeated_fault && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
            <AlertTriangle className="h-4 w-4" /> Tekrarlayan arıza tespit edildi
          </div>
        )}
      </div>

      <Tabs defaultValue="devices">
        <TabsList>
          <TabsTrigger value="devices" data-testid="tab-devices">Cihazlar ({devices.length})</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">Servis Geçmişi ({jobs.length})</TabsTrigger>
          <TabsTrigger value="rewards" data-testid="tab-rewards">Kupon & Sadakat</TabsTrigger>
        </TabsList>

        <TabsContent value="devices">
          <Card className="p-5 border border-slate-200 shadow-none">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-semibold">Kayıtlı Cihazlar</h3>
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="add-device-button" className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Cihaz Ekle</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Yeni Cihaz</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Marka *</Label>
                      <Select value={form.brand_id} onValueChange={(v) => setForm({ ...form, brand_id: v })}>
                        <SelectTrigger data-testid="device-brand-select"><SelectValue placeholder="Marka seçin" /></SelectTrigger>
                        <SelectContent>{brands.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label>Tip</Label>
                        <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="kombi">Kombi</SelectItem>
                            <SelectItem value="klima">Klima</SelectItem>
                            <SelectItem value="diger">Diğer</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div><Label>Model</Label><Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} /></div>
                    </div>
                    <div><Label>Seri No</Label><Input value={form.serial_no} onChange={(e) => setForm({ ...form, serial_no: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                      <div><Label>Kurulum Tarihi</Label><Input type="date" value={form.install_date} onChange={(e) => setForm({ ...form, install_date: e.target.value })} /></div>
                      <div><Label>Garanti Bitiş</Label><Input type="date" value={form.warranty_until} onChange={(e) => setForm({ ...form, warranty_until: e.target.value })} /></div>
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
                    <Button onClick={addDevice} className="bg-blue-600 hover:bg-blue-700">Kaydet</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {devices.length === 0 ? (
              <div className="text-center text-slate-500 py-12">Cihaz kaydı yok</div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {devices.map(d => (
                  <div key={d.id} className="p-4 rounded-md border border-slate-200 bg-slate-50/40 flex items-start gap-3">
                    {d.brand_logo ? (
                      <img src={d.brand_logo} alt={d.brand_name} className="h-10 w-10 object-contain rounded bg-white border border-slate-200 p-1" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-slate-200 text-slate-600 text-xs flex items-center justify-center font-semibold">{d.brand_name?.[0]}</div>
                    )}
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{d.brand_name}</div>
                      <div className="text-sm text-slate-600">{d.model} • {d.type}</div>
                      <div className="text-xs text-slate-500 font-mono">{d.serial_no || "S/N: -"}</div>
                      {d.warranty_until && (
                        <div className={`text-xs mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded ${isWarrantyActive(d) ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                          <ShieldAlert className="h-3 w-3" />
                          {isWarrantyActive(d) ? "Garanti aktif" : "Garanti bitti"} — {formatDate(d.warranty_until)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="history">
          <Card className="border border-slate-200 shadow-none overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-left text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 font-semibold">Kayıt No</th>
                  <th className="px-4 py-3 font-semibold">Arıza</th>
                  <th className="px-4 py-3 font-semibold">Durum</th>
                  <th className="px-4 py-3 font-semibold text-right">Tutar</th>
                  <th className="px-4 py-3 font-semibold">Tarih</th>
                </tr>
              </thead>
              <tbody>
                {jobs.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">Servis kaydı yok</td></tr>}
                {jobs.map(j => (
                  <tr key={j.id} className="border-b border-slate-100 hover:bg-slate-50/60">
                    <td className="px-4 py-3"><Link to={`/admin/jobs/${j.id}`} className="font-mono text-blue-700 hover:underline">{j.job_number}</Link></td>
                    <td className="px-4 py-3 max-w-xs truncate">{j.fault_description}</td>
                    <td className="px-4 py-3"><span className={`status-pill border ${STATUS_LABELS[j.status]?.color}`}>{STATUS_LABELS[j.status]?.label}</span></td>
                    <td className="px-4 py-3 text-right font-mono">{formatTL(j.total_amount)}</td>
                    <td className="px-4 py-3">{formatDate(j.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="rewards"><CustomerRewards customerId={id} jobs={jobs} /></TabsContent>
      </Tabs>
    </div>
  );
}
